import React, { useMemo, useState } from 'react';
import './Login.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

const RECOVERY_CUTOFF_LABEL = '22/03/2026';

function readRecoveryParams(search, hash) {
  const searchParams = new URLSearchParams(search || '');
  const hashValue = (hash || '').startsWith('#') ? hash.slice(1) : (hash || '');
  const hashParams = new URLSearchParams(hashValue);

  const getParam = (name) => hashParams.get(name) || searchParams.get(name) || '';

  return {
    accessToken: getParam('access_token'),
    refreshToken: getParam('refresh_token'),
    flowType: getParam('type'),
  };
}

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { resetPasswordWithRecovery } = useAuth();
  const { toast } = useToast();

  const recoveryParams = useMemo(
    () => readRecoveryParams(location.search, location.hash),
    [location.search, location.hash]
  );

  const hasValidTokens = Boolean(recoveryParams.accessToken && recoveryParams.refreshToken);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!hasValidTokens) {
      toast({
        variant: 'destructive',
        title: 'Link invalido',
        description: 'O link de recuperacao nao contem os tokens necessarios.',
      });
      return;
    }
    if (!newPassword || !confirmPassword) {
      toast({
        variant: 'destructive',
        title: 'Senha obrigatoria',
        description: 'Preencha e confirme a nova senha.',
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        variant: 'destructive',
        title: 'Senha diferente',
        description: 'As senhas informadas nao coincidem.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPasswordWithRecovery(
        recoveryParams.accessToken,
        recoveryParams.refreshToken,
        newPassword
      );

      toast({
        title: 'Senha redefinida',
        description: 'Sua senha foi atualizada. Faca login novamente.',
      });
      navigate('/login', { replace: true });
    } catch (err) {
      if (err?.name === 'AbortError') {
        return;
      }

      const statusCode = Number(err?.statusCode || err?.status || 0);
      let description = err?.getUserFriendlyMessage?.() || err?.message || 'Nao foi possivel redefinir a senha.';

      if (statusCode === 401) {
        description = 'Link de recuperacao invalido ou expirado.';
      } else if (statusCode === 422) {
        description = 'Link de recuperacao incompleto. Solicite um novo email.';
      } else if (statusCode === 403) {
        description = `Recuperacao por email disponivel apenas para contas criadas a partir de ${RECOVERY_CUTOFF_LABEL}.`;
      }

      toast({
        variant: 'destructive',
        title: 'Erro ao redefinir senha',
        description,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="login-container">
      <div className="login-box auth-card shadow-2xl border border-gray-100">
        <h1 className="login-title">CorreFood</h1>
        <p className="login-subtitle">Definir nova senha</p>
        {recoveryParams.flowType && recoveryParams.flowType !== 'recovery' && (
          <p className="auth-helper-text text-xs text-gray-500 mt-1">
            Fluxo recebido: {recoveryParams.flowType}
          </p>
        )}

        {!hasValidTokens && (
          <p className="auth-error-text text-red-500 text-sm mt-2">
            Link invalido ou incompleto. Solicite um novo email de recuperacao.
          </p>
        )}

        <form onSubmit={handleSubmit} className="auth-form space-y-4">
          <div>
            <input
              className="auth-input w-full p-3 rounded-lg border focus:ring-2 focus:ring-orange-500 outline-none"
              type="password"
              placeholder="Nova senha"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
            />
            <p className="auth-helper-text text-xs text-gray-500 mt-1">
              Minimo 8 caracteres com maiuscula, minuscula, numero e caractere especial.
            </p>
          </div>

          <input
            className="auth-input w-full p-3 rounded-lg border focus:ring-2 focus:ring-orange-500 outline-none"
            type="password"
            placeholder="Confirmar nova senha"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
          />

          <button
            type="submit"
            disabled={isSubmitting || !hasValidTokens}
            className="auth-submit-btn w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Salvando...' : 'Redefinir senha'}
          </button>
        </form>

        <div className="login-footer">
          <p className="auth-footer-text text-center text-sm text-gray-600 mt-4">
            Voltar para{' '}
            <a href="/login" className="text-orange-500 hover:underline font-semibold">
              login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
